import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'

export function ProtectionDialog({
  accountId,
  onClose,
}: {
  accountId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const accounts = useQuery({ queryKey: ['mail-accounts'], queryFn: () => api.listMailAccounts() })
  const folders = useQuery({
    queryKey: ['mail-folders', accountId],
    queryFn: () => api.listMailFolders(accountId),
  })
  const account = accounts.data?.accounts.find((item) => item.id === accountId)
  const update = useMutation({
    mutationFn: async ({
      folderId,
      protected: value,
    }: {
      folderId?: string
      protected: boolean
    }) =>
      folderId
        ? api.setMailFolderProtected(accountId, folderId, value)
        : api.setMailAccountProtected(accountId, value),
    onSuccess: async () => {
      // Clear cached search results as soon as the protection boundary changes.
      await qc.cancelQueries({ queryKey: ['mail-search'] })
      qc.setQueriesData({ queryKey: ['mail-search'] }, { messages: [] })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['mail-accounts'] }),
        qc.invalidateQueries({ queryKey: ['mail-folders', accountId] }),
        qc.invalidateQueries({ queryKey: ['mail-search'] }),
      ])
    },
  })
  const error = update.error ?? accounts.error ?? folders.error
  return (
    <Dialog
      open
      onClose={onClose}
      title="Schutz bearbeiten"
      description={account?.label}
      footer={<Button onClick={onClose}>Fertig</Button>}
    >
      <p className="text-sm text-text-secondary mb-4">
        Geschützte Mails bleiben für Dich lesbar. KI, automatische Übergaben und Volltextsuche sind
        gesperrt.
      </p>
      {error && (
        <p role="alert" className="text-sm text-danger mb-3">
          {error.message}
        </p>
      )}
      {!account ? (
        <p className="text-sm text-text-tertiary">Konto wird geladen …</p>
      ) : (
        <>
          <label className="flex items-center justify-between gap-3 min-h-12 py-3 border-b border-border-subtle">
            <span className="text-sm font-medium">Ganzes Konto schützen</span>
            <input
              type="checkbox"
              role="switch"
              className="peer sr-only"
              aria-checked={account.protected}
              checked={account.protected}
              disabled={update.isPending}
              onChange={(e) => update.mutate({ protected: e.target.checked })}
            />
            <SwitchTrack />
          </label>
          <p className="text-xs text-text-tertiary mt-4">
            {account.protected
              ? 'Alle Ordner sind geschützt. Einzelne Einstellungen bleiben beim Lösen erhalten.'
              : 'Einzelne Ordner schützen'}
          </p>
          {folders.isPending && (
            <p className="text-sm text-text-tertiary">Ordner werden geladen …</p>
          )}
          {folders.data?.folders.map((folder) => (
            <label
              key={folder.id}
              className={`flex items-center justify-between gap-3 min-h-12 py-3 ${account.protected ? 'opacity-50' : ''}`}
            >
              <span className="text-sm break-all">{folder.name}</span>
              <input
                type="checkbox"
                role="switch"
                className="peer sr-only"
                aria-checked={account.protected || folder.protected}
                checked={account.protected || folder.protected}
                disabled={account.protected || update.isPending}
                onChange={(e) =>
                  update.mutate({ folderId: folder.id, protected: e.target.checked })
                }
              />
              <SwitchTrack />
            </label>
          ))}
        </>
      )}
    </Dialog>
  )
}

function SwitchTrack() {
  return (
    <span
      aria-hidden="true"
      className="relative h-6 w-11 shrink-0 rounded-full bg-bg-overlay border border-border-default peer-checked:bg-amber-500 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-amber-500 after:absolute after:left-1 after:top-[3px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5"
    />
  )
}
